import { useMemo } from 'react'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button, Field, FieldError, FieldLabel, Input } from '@clouway/ui'

import type { ChangePasswordInput } from '../types'

const buildSchema = (required: string, mismatch: string) =>
  z
    .object({
      password: z.string().min(1, required),
      newPassword: z.string().min(1, required),
      confirmNewPassword: z.string().min(1, required),
    })
    .refine((d) => d.newPassword === d.confirmNewPassword, {
      message: mismatch,
      path: ['confirmNewPassword'],
    })

export type PasswordFormValues = z.infer<ReturnType<typeof buildSchema>>

export function PasswordForm({ onSubmit }: { onSubmit: SubmitHandler<ChangePasswordInput> }) {
  const { t } = useTranslation('feature')
  const schema = useMemo(() => buildSchema(t('password.required'), t('password.mismatch')), [t])
  const { control, handleSubmit, formState, reset } = useForm<PasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', newPassword: '', confirmNewPassword: '' },
  })

  const fields: { name: keyof PasswordFormValues; label: string }[] = [
    { name: 'password', label: 'password.current' },
    { name: 'newPassword', label: 'password.new' },
    { name: 'confirmNewPassword', label: 'password.confirm' },
  ]

  const submit: SubmitHandler<PasswordFormValues> = async (values) => {
    await onSubmit(values)
    reset()
  }

  return (
    <form className="mx-auto flex w-full max-w-2xl flex-col gap-4" onSubmit={handleSubmit(submit)}>
      {fields.map(({ name, label }) => (
        <Controller
          key={name}
          control={control}
          name={name}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel htmlFor={`password-${name}`}>{t(label as 'password.current')}</FieldLabel>
              <Input id={`password-${name}`} type="password" {...field} />
              {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
            </Field>
          )}
        />
      ))}

      <Button type="submit" disabled={formState.isSubmitting}>
        {formState.isSubmitting ? t('password.submitting') : t('password.submit')}
      </Button>
    </form>
  )
}
